import type {Ref} from 'vue';
import type {OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import {createContext} from '@/shared/createContext';
import type {
    BarlineSide,
    BarStyleChoice,
    ChordToneExtreme,
    ClipboardNote,
    KeySignatureInfo,
    MeasureStructureInfo,
    NoteInfo,
    PickupState,
    TimeSignatureInfo,
    TriadReductionMode,
} from '@/composables/musicXmlDom';
import type {LyricCommitMode, VerseNumber} from '@/composables/osmd/useLyricEntry';
import type {DeleteMode} from '@/composables/osmd/useScoreEditActions';

export type EditTab = 'note' | 'chord' | 'key' | 'time' | 'structure' | 'measures' | 'bass';

/**
 * Selektions-/Edit-Kontext der interaktiven Notenvorschau. `useOsmdInteractiveScore` stellt ihn
 * bereit (`provideScoreEditContext`); `ScoreEditToolbar`, `PlaybackControlsOverlay`, die Akkord-/Tonart-/
 * Taktart-Views, `LyricEntryOverlay`, `BassLyricPopover` und `NoteActionsOverlay` injizieren ihn –
 * so muss der State nicht per Prop durchgereicht werden, bleibt aber pro Vorschau-Instanz isoliert
 * (kein globaler Store: der State hängt an EINER OSMD-Instanz – `selectedNoteX/Y` sind Pixel
 * relativ zu deren Container – und wird bei jedem Inhaltswechsel bewusst verworfen).
 *
 * **Was hier NICHT hineingehört (kritisch – nicht wieder zusammenlegen):**
 * - **Der Audio-Player** → {@link ./playbackContext} (`ScorePlaybackContext`). Er existiert nur
 *   auf der Eingabe-Vorschau, und sein einziger Konsument `OsmdPlaybackControls` nutzt 16/16
 *   Player- und 0 Edit-Member.
 * - **Was nur der Host (`MusicXmlPreview`) braucht** – `containerRef`/`scrollContainerRef`,
 *   `error`, die `handleContainer*`-Handler. Die liefert der **Rückgabewert** von
 *   `useOsmdInteractiveScore`; kein Kind injiziert sie je.
 *
 * **Wann ein eigener Kontext?** Wenn (a) der Bereich nur auf einer der beiden Vorschauen
 * existiert (dann optionaler Kontext mit `null`-Fallback, siehe
 * {@link ./toolbarExtensionsContext}), ODER (b) ein Konsumenten-Teilbaum
 * (fast) nichts anderes hieraus nutzt – der Schnitt also Injects ENTFERNT statt welche
 * hinzuzufügen. Müsste irgendein Konsument zwei Injects machen, um EINE Aufgabe zu erledigen,
 * bleiben die Member hier (deshalb sind Selektion, Edit-Aktionen und Gesangstext NICHT getrennt:
 * 5 von 8 Konsumenten spannen Selektion + Edit-Aktionen).
 */
export interface ScoreEditContext {
    // ---- Selektions-/Edit-State ----
    getOsmd: () => OpenSheetMusicDisplay | null;
    /** Eindeutige interne Taktposition; `selectedMeasure` ist nur die gedruckte XML-Nummer. */
    selectedMeasureIndex: Ref<number | null>;

    selectedMeasure: Ref<number | null>;
    selectedNote: Ref<NoteInfo | null>;
    /** Geordnete Mehrfachauswahl (dedupliziert per noteKey; enthält die primäre `selectedNote`).
     *  Einzelauswahl = Liste der Größe 1. */
    selectedNotes: Ref<NoteInfo[]>;
    /** Per Ganztakt-Auswahl gewählte Takte; primärer Takt bleibt `selectedMeasure`. */
    selectedMeasures: Ref<number[]>;
    /** Pixel-Position (X/Y) der angeklickten Note (relativ zum Scroll-Container der Vorschau),
     *  Anker der schwebenden Editoren; `null` = keine Note ausgewählt. */
    selectedNoteX: Ref<number | null>;
    selectedNoteY: Ref<number | null>;
    currentChord: Ref<string | null>;
    currentKeySignature: Ref<KeySignatureInfo | null>;
    currentTimeSignature: Ref<TimeSignatureInfo | null>;
    /** `'note'` = Menü, sonst der im `NoteActionsOverlay` geöffnete Takt-Editor. */
    activeTab: Ref<EditTab>;
    // ---- Selektions-/Edit-Aktionen ----
    openChordEditor: () => void;
    openKeySignatureEditor: () => void;
    openTimeSignatureEditor: () => void;
    // ---- Struktur: Taktstriche / Wiederholungen / Volta / Takt einfügen+löschen ----
    /** Struktur des ausgewählten Takts (Wiederholungen/Volta/Taktstrich), reaktiv aus dem XML
     *  abgeleitet (`getMeasureStructureInfo`) – bleibt nach jedem Commit/Undo von selbst frisch. */
    currentMeasureStructure: Ref<MeasureStructureInfo | null>;
    /** Vom Kanten-Doppelklick vorgewählte Taktseite (visueller Akzent im Editor);
     *  `null` = per Menü/Toolbar geöffnet. */
    structureSide: Ref<BarlineSide | null>;
    /** Öffnet den Reiter „Wiederholungen & Taktstriche" für den ausgewählten Takt. */
    openStructureEditor: () => void;
    /** Öffnet den Reiter „Takte" (einfügen/löschen) für den ausgewählten Takt. */
    openMeasuresEditor: () => void;
    /** Setzt/entfernt `|:` (`side='start'`) bzw. `:|` (`side='end'`, optional `times ≥ 2`). */
    setRepeat: (side: 'start' | 'end', enabled: boolean, times?: number) => void;
    /** Setzt/entfernt die Durchgangs-Anzahl eines vorhandenen `:|` (`null` = Attribut entfernen). */
    setRepeatTimes: (times: number | null) => void;
    /** Stil des rechten Taktstrichs (Normal/Doppelstrich/Schlussstrich). */
    setBarStyle: (style: BarStyleChoice) => void;
    /** Setzt eine Volta-Klammer über einen Taktbereich; überlappende Klammern werden ersetzt. */
    setEnding: (endingNumber: number, startIndex: number, endIndex: number) => void;
    /** Entfernt die ganze Klammer, der der ausgewählte Takt angehört. */
    removeEnding: () => void;
    /** Fügt einen pausengefüllten Takt vor/nach dem ausgewählten ein (Takt-Kontext folgt). */
    insertMeasure: (position: 'before' | 'after') => void;
    /** Löscht den ausgewählten Takt score-weit (Undo über die lokale History). */
    deleteMeasure: () => void;
    /** Ob Takt 1 ein Auftakt ist und ob sich das umschalten lässt (`pickupState`), reaktiv aus dem
     *  XML – wie `currentMeasureStructure`. */
    pickupInfo: Ref<PickupState | null>;
    /** Takt 1 als Auftakt markieren bzw. aufheben (`setPickupInMusicXml`); meldet per Toast. */
    setPickup: (enabled: boolean) => void;
    /** Auswahl auf die nächste/vorige Note verschieben (über Taktgrenzen hinweg; am Stück-Rand
     *  geclampt). Setzt Note-/Takt-Highlight und Panel-Position konsistent. */
    selectNextNote: () => void;
    selectPreviousNote: () => void;
    /** Auswahl **kumulativ** um die vorige/nächste Note (`dir` -1/+1) erweitern (Strg+Pfeil); die
     *  primäre Note wandert mit, wiederholtes Aufrufen vergrößert die Auswahl fortlaufend. */
    extendSelectionByNote: (dir: 1 | -1) => void;
    /** Auswahl **kumulativ** um den vorigen/nächsten Takt (`dir` -1/+1) erweitern (Strg+Umschalt+Pfeil):
     *  alle Noten des Nachbartakts kommen dazu; `selectedMeasures` folgt abgeleitet. */
    extendSelectionByMeasure: (dir: 1 | -1) => void;
    /** Auswahl **kumulativ** auf den Part/die Notenzeile darüber/darunter (`dir` -1 = oben, +1 = unten)
     *  erweitern (Strg+Umschalt+Pfeil hoch/unten) – nur bei mehrteiligen Stücken. */
    extendSelectionByPart: (dir: 1 | -1) => void;
    /** Ob der aktuell gerenderte Score mehrere Parts/Notenzeilen hat (steuert die Part-Buttons). */
    hasMultipleParts: Ref<boolean>;
    /** Auswahl auf den nächsten Takt verschieben (am Stück-Rand geclampt). Hebt eine bestehende
     *  Noten-Auswahl auf und aktualisiert das Takt-Highlight. */
    selectNextMeasure: () => void;
    clearSelection: () => void;
    /** Löscht die gesamte (Mehrfach-)Auswahl. Auf der Eingabe-Vorschau fragt es, ob ganz gelöschte
     *  Zählzeiten zur Pause werden oder die Folgenoten nachrücken (`deleteChoicePending`);
     *  `shift: true` (Umschalt+Entf) rückt ohne Frage nach. Sonst längenerhaltend zur Pause. */
    requestDelete: (opts?: {shift?: boolean}) => void;
    /** Der Lösch-Dialog ist offen (gerendert von `DeleteChoiceDialog`). */
    deleteChoicePending: Ref<boolean>;
    /** Antwort des Lösch-Dialogs: `'rest'` = Pause, `'shift'` = nachrücken, `null` = abbrechen. */
    resolveDeleteChoice: (mode: DeleteMode | null) => void;
    /** „Teilen" (Strg+Umschalt+S): halbiert die übergebene Note **oder Pause** und fügt eine zweite,
     *  gleich lange direkt danach ein (längenerhaltend, Taktdauer bleibt gültig). */
    splitNote: (note: NoteInfo) => void;
    /** „Zusammenführen" (Strg+Umschalt+M, Gegenstück zum Teilen): führt die übergebene Note **oder Pause**
     *  mit der direkt folgenden derselben Stimme im selben Takt zu **einer** Note mit der Summendauer
     *  zusammen (längenerhaltend). No-op bei der letzten Note im Takt oder wenn die Summe keine einzelne
     *  (punktierte) Note ergibt. */
    mergeNote: (note: NoteInfo) => void;
    /** „Haltebögen zusammenfassen" (Strg+Umschalt+H): jede per Haltebogen gebundene Kette gleicher
     *  Tonhöhe im Takt, die die Auswahl berührt, wird eine Note (`mergeTiedNotesInMusicXml`). Meldet
     *  das Ergebnis per Toast. */
    mergeTiedNotes: () => void;
    /** „Länge halbieren" (Q) der ausgewählten Noten; die Folgenoten der Stimme rücken nach vorne
     *  (über Taktlinien, `scaleNoteDurationsInMusicXml`). */
    halveDurations: () => void;
    /** „Länge verdoppeln" (W); die Folgenoten der Stimme rücken nach hinten, nichts wird
     *  überschrieben, am Stückende werden bei Bedarf Takte angehängt. */
    doubleDurations: () => void;
    /** Kopierte Sequenz (Mehrfach-Kopieren); leer = nichts kopiert. */
    copiedSequence: Ref<ClipboardNote[]>;
    /** Kopiert die gesamte (Mehrfach-)Auswahl als Sequenz in die Zwischenablage (Strg+C). */
    copySelection: () => void;
    /** Ersetzt die Auswahl durch die Kopie (Strg+Umschalt+V, taktlängen-neutral): Einzelauswahl →
     *  ganze Sequenz inkl. Rhythmus (Viertel→2 Achtel); Mehrfachauswahl → zyklisch nur Tonhöhe. */
    pasteNoteReplace: () => void;
    /** Fügt die kopierte Sequenz nach der primären Auswahl ein (Strg+V, taktlängen-neutral). */
    pasteSelection: () => void;
    /** Transponiert die gesamte (Mehrfach-)Auswahl um ±`deltaSemitones` Halbtöne. */
    transposeSelection: (deltaSemitones: number) => void;
    /** „Diatonisch hinzufügen" (Alt+↑/↓): fügt zu jeder ausgewählten Note den um `interval`
     *  (±2/3/5/6/8 = Sekunde…Oktave) diatonisch verschobenen Ton als Akkord-Begleiter hinzu
     *  (Tonart = geltende Vorzeichnung; taktlängen-neutral). */
    addDiatonicTones: (interval: number) => void;
    /** „Dreiklang reduzieren": ausgewählte Dreiklänge auf zwei Töne reduzieren
     *  (`low_mid` / `low_high` / `mid_high`); Dyaden/Vierklänge bleiben unberührt. */
    reduceTriads: (mode: TriadReductionMode) => void;
    /** „Höchste/Niedrigste Note entfernen": entfernt aus jeder ausgewählten Akkord-Gruppe den
     *  höchsten bzw. tiefsten Ton – größenunabhängig (jeder Akkord ab zwei Tönen). */
    removeExtremeChordTones: (which: ChordToneExtreme) => void;
    /** Toggelt einen Bindebogen (Tie/Slur automatisch) zwischen Auswahl und nächster Note (Strg+B). */
    toggleTie: () => void;
    saveChord: (data: { measureNumber: number; measureIndex?: number; chord: string }) => void;
    // ---- Bass-Vorschau-System (nur auf der Eingabe-Vorschau vorhanden) ----
    /** Gibt es ein erzeugtes Bass-System? Steuert, ob der Reiter „Bass" überhaupt erscheint. */
    hasBassPreview: Ref<boolean>;
    /** Spielweise je Takt-Index, aus dem Score gelesen (`wechselbass`, wenn kein Eintrag). */
    bassStyles: Ref<Record<number, string>>;
    /** Oeffnet den Reiter „Bass“ fuer den ausgewaehlten Takt. */
    openBassStyleEditor: () => void;
    /** Setzt die Spielweise eines Takts. Die Vorschau ruft dafür KEINEN Endpunkt – sie meldet nach
     *  oben (`MusicXmlPreview` → `PreviewPane` → Store), wo das Bass-System neu gebaut wird. */
    setBassStyle: (measureIndex: number, style: string) => void;
    removeChord: (measureNumber: number, measureIndex?: number) => void;
    saveKeySignature: (key: KeySignatureInfo) => void;
    saveTimeSignature: (time: TimeSignatureInfo) => void;
    /** Prüft, ob der Inhalt des Zieltakts länger ist als das gewählte Metrum – dann fallen beim
     *  Ein-Takt-Wechsel (`saveTimeSignature`) Noten am Taktende weg; die View zeigt das als Hinweis. */
    checkTimeSignatureOverfull: (
        measureNumber: number, beats: number, beatType: number, measureIndex?: number,
    ) => boolean;
    // ---- Taktart ändern + Noten neu aufteilen (nur Eingabe-Vorschau) ----
    /** Bietet diese Vorschau das Neuaufteilen überhaupt an (Host hat `onRebar` gesetzt)? */
    canRebar: Ref<boolean>;
    /** Grund, warum das Neuaufteilen gerade gesperrt ist (`''` = erlaubt), z. B. ungespeicherte
     *  Änderungen – die gingen beim Übernehmen des Server-Ergebnisses sonst still verloren. */
    rebarBlockedReason: Ref<string>;
    /** Ab dem ausgewählten Takt bis Stückende die Taktart setzen und die Noten neu aufteilen. Die
     *  Vorschau ruft dafür KEINEN Endpunkt – sie meldet nach oben (`MusicXmlPreview` →
     *  `PreviewPane` → Store), wie `setBassStyle`. */
    rebarFromMeasure: (beats: number, beatType: number) => void;
    // ---- Gesangstext (Strophen-Lyrics; auf BEIDEN Vorschauen verfügbar) ----
    // Bewusst im generischen Kontext statt im `toolbarExtensionsContext` (dort liegt nur die
    // Freischaltung): dessen Grip-/Bass-Slots existieren "nur auf der Ausgabe-Vorschau" –
    // Gesangstext gibt es auf beiden.
    /** Strophe, die gerade beschrieben wird (`'1'`/`'2'`; 3 + 10–13 sind Engine-reserviert). */
    activeVerse: Ref<VerseNumber>;
    /** Läuft eine Eingabesitzung? Steuert das schwebende Eingabefeld. */
    isLyricActive: Ref<boolean>;
    /** Text der aktuellen Note (v-model des Eingabefelds). */
    lyricDraft: Ref<string>;
    /** Entwurf sichern und je nach Modus weiterrücken (siehe {@link LyricCommitMode}). */
    commitLyric: (mode: LyricCommitMode) => void;
    /** Sitzung beenden; sichert den letzten Entwurf und meldet Änderungen nach oben. */
    endLyricEntry: () => void;
}

export const [injectScoreEditContext, provideScoreEditContext] =
    createContext<ScoreEditContext>('MusicXmlPreview', 'ScoreEditContext');
